/* eslint max-len: ["error", {"code": 100,"ignoreStrings": true,"ignoreTemplateLiterals": true }] */

import { FAQProps } from 'lib/components/FAQ/FAQ'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { NewsletterSignupProps } from 'lib/components/NewsletterSignup/NewsletterSignup'

import { StructuredData } from '@/utils/brand'

export interface FAQPageContent {
  hero: NarrativeHeroProps
  faq: FAQProps
  newsletter: NewsletterSignupProps
  structuredData?: StructuredData
}

const hero: NarrativeHeroProps = {
  header: 'Frequently Asked Questions',
  subHeader: 'Browse common queries or select a topic below to narrow your search.',
}

const faq: FAQProps = {
  subtitle: 'Please choose a topic below to better pinpoint your question you may have.',
  title: (
    <>
      Have questions?
      <br />
      <b>We have answers.</b>
    </>
  ),
  categories: [
    {
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      categoryName: 'Packing & Preparation',
      categoryId: 4,
    },
    {
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
  ],
  items: [
    {
      question: 'What is ShipSticks?',
      answer: (
        <>
          <p>
            ShipSticks is a door-to-door shipping service that helps you travel more easily by
            sending your golf clubs, luggage, and other gear ahead of your trip. We pick up from
            your home, office, golf course, or hotel and deliver directly to your destination.
          </p>

          <p>
            By shipping ahead with ShipSticks, you can skip the stress of hauling heavy bags through
            airports, waiting at baggage claim, and dealing with lost or delayed luggage.
          </p>
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'How do I create an account?',
      answer: (
        <>
          Navigate to &apos;Log In&apos; in the top right corner of your screen. You can either
          create a new account or sign in to an existing one, but you will need one to book.
          Creating an account requires no commitment and only takes a few moments to complete.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'Is it free to create an account?',
      answer: (
        <>
          Yes, signing up is completely free! There&apos;s no cost or commitment to create an
          account.
        </>
      ),
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      question: 'Who can use ShipSticks?',
      answer: (
        <>
          Anyone can use ShipSticks! Whether you&apos;re a seasoned golfer, a first-time golf
          traveler, or shipping on behalf of someone else, our service is designed to make travel
          easier for everyone.
        </>
      ),
      categoryName: 'General Questions',
      categoryId: 2,
    },
    {
      question: 'How do I book a shipment?',
      answer: (
        <>
          Click here to get started. Simply enter your pickup and delivery locations, select your
          travel dates, and choose the service that best fits your needs.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'What happens after I book?',
      answer: (
        <>
          Once you complete your booking, you&apos;ll receive your shipping labels and step-by-step
          instructions.
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'How many days in advance should I schedule my gear to arrive?',
      answer: (
        <>
          We suggest scheduling your gear to arrive at least one business day before your first
          round. For example, if your tee time is on Monday, plan to have your clubs delivered by
          the Friday before.
        </>
      ),
    },
    {
      question: 'What if my local club isn’t a ShipSticks partner?',
      answer: (
        <>
          That’s ok! You can still use ShipSticks, even if your local club isn’t an official
          partner. We do, however, recommend suggesting the service to your club pro, so you can
          start enjoying the convenience of shipping directly from your local pro shop.
        </>
      ),
    },
    {
      question: 'What’s the difference between one-way and round-trip shipping?',
      answer: (
        <>
          One-way shipping means your golf clubs or gear are shipped from your starting point to
          your destination. Round-trip shipping includes shipping your gear to your destination and
          then back to your home or original location. There is no cost difference between two
          one-way shipments and a round-trip booking.
        </>
      ),
    },
    {
      question: 'What other items can I ship using ShipSticks?',
      answer: (
        <>
          <p>
            Travel completely bag-free by shipping your luggage along with your golf clubs. We can
            accommodate carry-on, checked, and oversized luggage.
          </p>
          <p>
            If you’re interested in shipping specialty items like push carts, or arranging group or
            event shipments, please{' '}
            <a href="https://www.shipsticks.com/contact">contact our team</a> so we can explore how
            to best accommodate your needs.
          </p>
        </>
      ),
    },
    {
      question: 'Where can I ship my gear?',
      answer: (
        <>
          You can send your shipment to any physical destination of your choosing, with the
          exception of P.O. boxes, airports, and countries we don’t currently service.
        </>
      ),
    },
    {
      question: 'What types of bags/containers are allowed?',
      answer: (
        <>
          Pack your golf clubs just like you would for the airlines, in a soft travel bag or hard
          case. You may also package your clubs in a box, but it is not required. Luggage should be
          packed in a suitcase, duffel bag, or box.
        </>
      ),
    },
    {
      question: 'How should I pack my clubs or luggage?',
      answer: (
        <>
          Click <a href="/pack">here</a> for more information on packing golf clubs and luggage.
        </>
      ),
    },
    {
      question: 'Can I lock my domestic shipment?',
      answer: (
        <>
          If you’re shipping domestically within the U.S., your bag may be locked. However, we
          recommend leaving it unlocked to avoid any potential delays.
        </>
      ),
    },
    {
      question: 'Can I lock my international shipment?',
      answer: (
        <>
          No, you cannot lock your bag when shipping internationally. All bags must be unlocked to
          ensure customs inspections can be performed if necessary.
        </>
      ),
    },
    {
      question: 'Are there size or weight limits?',
      answer: (
        <>
          <p>
            Yes. Please identify your bag’s maximum dimensions (length + width + height) and weight
            to determine which bag size category it fits into. If your shipment exceeds the size or
            weight limits for that category, please{' '}
            <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
              contact
            </a>{' '}
            our team - we’ll be happy to provide a custom quote for your shipment.
          </p>
          <div>
            <strong>
              <br />
              Examples:
              <br />
            </strong>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                If you have a suitcase that weighs <strong>24 lbs</strong> with a maximum dimension
                of
                <strong> 60 inches</strong>, it should be booked as <strong>Checked Luggage</strong>
                .
              </li>
              <li>
                If you have a golf bag that weighs <strong>55 lbs</strong> with a maximum dimension
                of
                <strong> 70 inches</strong>, it should be booked as an <strong>XL Golf Bag</strong>.
              </li>
            </ul>
          </div>
          <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-6 sm:px-2 md:px-6">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 pb-1 text-left">Bag Type</th>
                  <th className="border-b border-gray-200 pb-1 text-left">Max Dimensions</th>
                  <th className="border-b border-gray-200 pb-1 text-left">Max Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 py-1">Standard Golf Bag</td>
                  <td className="border-b border-gray-200 py-1">72 inches</td>
                  <td className="border-b border-gray-200 py-1">42 lbs</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 py-1">XL Golf Bag</td>
                  <td className="border-b border-gray-200 py-1">79 inches</td>
                  <td className="border-b border-gray-200 py-1">56 lbs</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 py-1">Carry-on Luggage</td>
                  <td className="border-b border-gray-200 py-1">45 inches</td>
                  <td className="border-b border-gray-200 py-1">25 lbs</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 py-1">Checked Luggage</td>
                  <td className="border-b border-gray-200 py-1">62 inches</td>
                  <td className="border-b border-gray-200 py-1">50 lbs</td>
                </tr>
                <tr>
                  <td className="py-1">Oversized Luggage</td>
                  <td className="py-1">75 inches</td>
                  <td className="py-1">65 lbs</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ),
    },
    {
      question: 'What happens if my bag is overweight?',
      answer: (
        <>
          If your bag weighs more than the limit for your chosen size, there could be extra fees and
          possible delays. To keep things running smoothly, we recommend sizing up if you’re unsure
          - better safe than sorry!
        </>
      ),
    },

    {
      question: 'What if I can’t weigh my bag?',
      answer: (
        <>
          Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your
          bag weighed. If the weight is significantly heavier than expected, additional fees may
          apply.
        </>
      ),
    },
    {
      question: 'How do I attach the shipping labels?',
      answer: (
        <>
          If you&apos;re using a hard travel case or box, attach your label with clear packing tape,
          making sure the barcode is visible and secure. If your bag doesn&apos;t have a hard
          surface, you&apos;ll need to place the label on a luggage baggage tag (also known as a fly
          tag), which you can pick up from your local FedEx, UPS, or DHL location, and attach it to
          the handle. Don&apos;t forget to put a second copy of your shipping label in your bag,
          too.
        </>
      ),
      categoryName: 'Packing & Preparation',
      categoryId: 4,
    },
    {
      question: 'What if I don’t have a printer?',
      answer: (
        <>
          You can email the shipping label to your local FedEx, UPS, or DHL for printing. Or, if
          time allows, for an additional fee we can mail printed labels directly to you - just give
          us a heads-up when placing your order.
        </>
      ),
    },
    {
      question: 'Does ShipSticks provide packaging?',
      answer: (
        <>
          We don’t supply packaging, but no worries - you can simply use your own hard case, soft
          travel bag, or box to ship your gear.
        </>
      ),
    },
    {
      question: 'Do I need a box?',
      answer: (
        <>
          Nope, no box needed! If you’re shipping a golf bag in an airline-approved soft travel bag
          or hard case, you’re good to go. Same goes for luggage - as long as it’s packed in a
          suitcase or duffle bag.
        </>
      ),
    },
    {
      question: 'Where do I get a box?',
      answer: (
        <>
          If you have a soft travel bag or hard case, you don’t need a box - that’s the beauty of
          our service. However, if you need a box, you can usually buy one at your local UPS or
          FedEx store for a small fee (prices vary by location). We also sell golf bag-sized boxes -
          just reach out to Customer Support to order. Here’s our pricing and shipping times for
          boxes:
          <br />
          Ground Shipping (5–7 Business Days)
          <br />
          1 Box: $29.99
          <br />
          2 Boxes: $34.99
          <br />
          Express Shipping (1–3 Business Days)
          <br />
          1 Box: $39.99
          <br />2 Boxes: $44.99
        </>
      ),
    },
    {
      question: 'Do I have to pack my bag differently if I am shipping internationally?',
      answer: (
        <>
          For international shipments, no locks are allowed, including TSA locks, since customs may
          need to inspect your package.
          <br />
          Here’s what you can include in your golf bag: clubs, bags, balls, golf shoes, and rain
          gear like umbrellas or jackets and pants. For luggage, you can pack worn clothing, new
          clothing with price tags removed, footwear, headwear, and undergarments.
        </>
      ),
    },
    {
      question: 'What can I include in my shipment?',
      answer: (
        <>
          Here’s what you can include in your golf bag: clubs, bags, balls, golf shoes, and rain
          gear like umbrellas or jackets and pants. For luggage, you can pack worn clothing, new
          clothing with price tags removed, footwear, headwear, and undergarments.
        </>
      ),
    },
    {
      question: 'What items are prohibited?',
      answer: (
        <>
          Some things are off-limits, like toiletries (soap, shampoo, toothpaste), medications,
          pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable items,
          electronics like rangefinders and GPS devices, batteries, alcohol or anything containing
          alcohol (including perfume and cologne), tobacco, firearms and ammunition, and food,
          vitamins, or other consumables.
          <br />
          Before shipping, please remove all price tags. For the full list of prohibited items,
          check out our Terms of Use.
        </>
      ),
    },
    {
      question: 'Can I add luggage to my order?',
      answer: (
        <>
          Yes! Adding pieces of luggage to your order is simple. Just scroll down to the traveler
          section, below the pickup and delivery dates, and you’ll see an option labeled Luggage
          Count. From the dropdown menu, select the number of pieces you want to ship and choose the
          type—carry-on, checked, or oversized bag.
        </>
      ),
    },
    {
      question: 'Can I add hotel check-in information to my shipping label?',
      answer: (
        <>
          Yes, under ‘Other Info’ on the booking page, you can type in any information you would
          like. For example: Confirmation Number XXXXX, Checking in XX/XX/XX, Guest of John Smith,
          etc.
        </>
      ),
    },
    {
      question: 'Who will be picking up my shipment?',
      answer: (
        <>
          One of our trusted logistics partners, either FedEx, UPS, or DHL, will pick up your clubs
          or luggage. Your shipping label will indicate which carrier will handle your pickup.
        </>
      ),
    },
    {
      question: 'Can I get my golf clubs picked up from my home?',
      answer: (
        <>
          Yes! We can pick your clubs up from your home, office, or golf course - or you can simply
          drop them off at your nearest carrier store for added convenience.
        </>
      ),
    },
    {
      question: 'Is there a fee for dropping off my golf clubs?',
      answer: (
        <>
          FedEx Offices and UPS Stores do not charge fees to drop off. However, some third-party
          pickup and drop-off locations may charge package handling fees. These fees are separate
          from our service and can vary depending on the location. To avoid surprises, we recommend
          calling ahead to check if any fees apply.
        </>
      ),
    },
    {
      question: 'Can I choose my carrier drop-off location?',
      answer: (
        <>
          While we assign your carrier (FedEx, UPS, or DHL) when you book, you’re free to drop off
          your shipment at any local location serviced by that carrier. Click here to find a
          drop-off location near you.
        </>
      ),
    },
    {
      question: 'Do I have to be home when my bags are picked up?',
      answer: (
        <>
          You don’t need to be home for pickup. Just leave your securely packed and labeled shipment
          outside your front door during the pickup window, at your discretion. If you don’t want to
          leave it unattended and can’t be home, you can drop it off at a local FedEx, UPS, or DHL
          store - just make sure it matches the carrier listed on your shipping label.
        </>
      ),
    },
    {
      question: 'Can I cancel my order?',
      answer: (
        <>
          Yes, you may cancel your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          cancel.
        </>
      ),
    },
    {
      question: 'Can I make changes to my order?',
      answer: (
        <>
          Yes! You can update your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          modify. Just make sure to do it before your shipment is in transit. Once it’s on the move,
          you’ll need to contact customer support to request changes, and fees may apply.
        </>
      ),
    },
    {
      question: 'How long does delivery take?',
      answer: (
        <>
          Delivery times vary depending on the service level you choose. Ground shipments typically
          arrive within 2 to 6 business days, depending on the route, while expedited options can
          get your gear there even faster - some even as quickly as the next business day. After
          pickup, you’ll receive tracking updates so you can monitor your shipment every step of the
          way.
        </>
      ),
    },
    {
      question: 'Is a signature required upon delivery?',
      answer: (
        <>
          A signature is not required, but it’s ultimately up to the driver’s discretion. If the
          driver considers the delivery area secure or your destination has a shipping/receiving
          area, your shipment will be delivered without a signature. If this is a concern, we
          recommend shipping to a supervised location, like a business, golf course, resort, friend
          or family member, or carrier store (fees may apply).
        </>
      ),
    },
    {
      question: 'Does ShipSticks deliver on weekends?',
      answer: (
        <>
          Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled
          during regular business days when all of our partnered carriers are operating. That said,
          depending on the route, your gear might still arrive on a weekend.
        </>
      ),
    },
    {
      question:
        'Do I need to confirm that the golf course, resort, ' +
        'rental home, or hotel will accept my shipment?',
      answer: (
        <>
          Before placing your order, we do recommend reaching out to your destination to let them
          know about your shipment and your expected arrival date.
        </>
      ),
    },
    {
      question: 'Do hotels and golf courses charge to receive/hold my gear?',
      answer: (
        <>
          Policies vary by location. Some properties may charge a fee for receiving or holding
          shipments, so it’s best to check directly with them beforehand.
        </>
      ),
    },
    {
      question:
        'Can I have my bag returned to a different address than the one ' +
        'it was originally shipped from?',
      answer: (
        <>
          That’s easy! Just book two separate one-way shipments - one to send your bag out, and
          another to have it returned to a different address. Don’t worry, the cost is the same as a
          round-trip shipment. This also works if your trip has multiple stops and you need your bag
          sent to different places along the way.
        </>
      ),
    },
    {
      question: 'How do I track my shipment?',
      answer: (
        <>
          We&apos;ll keep you posted with email updates and optional text alerts when your shipment
          is picked up, out for delivery, and delivered. You&apos;ll also find a tracking link in
          your confirmation email. To track through your account, simply:
          <br />
          Visit www.ShipSticks.com
          <br />
          Log in (top-right corner)
          <br />
          Go to My Account {'>'} Shipments
          <br />
          Select the shipment you&apos;d like to track
        </>
      ),
      categoryName: 'How it Works',
      categoryId: 1,
    },
    {
      question: 'Will I get notifications for delivery updates?',
      answer: (
        <>
          Yes! You’ll receive real-time tracking notifications via email so you always know where
          your shipment is and when to expect delivery. You can opt-in to receive notifications via
          text as well.
        </>
      ),
    },
    {
      question: 'What if my shipment is delayed or lost?',
      answer: (
        <>
          Although delays and losses are rare, if we suspect either might have occurred, our
          customer support team will reach out to you. We’ll promptly investigate and work to
          resolve the issue as quickly as possible.
        </>
      ),
    },
    {
      question: 'Do you ship internationally?',
      answer: (
        <>We service 180 countries and territories, and handle all the tricky customs paperwork.</>
      ),
    },
    {
      question: 'How much does it cost to ship golf clubs?',
      answer: (
        <>
          The cost to ship golf clubs varies depending on factors like your shipping distance and
          the service level you choose. To get an exact quote, simply enter your pickup and delivery
          locations on our website or contact our customer support team for personalized assistance.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'Is insurance included? Can I add more coverage?',
      answer: (
        <>
          Domestic shipments come with $1,000 of insurance for golf bags and $500 for luggage.
          International shipments include $500 for either. Need more coverage? You can add extra for
          a small fee. We recommend choosing an amount that reflects the full value of your gear and
          the bag it’s packed in.
        </>
      ),
    },
    {
      question: 'What is covered under insurance?',
      answer: (
        <>
          Ship Sticks&apos; shipment insurance protects against complete loss of your shipped item
          or against any physical damage to qualified items that has occurred while in transit. For
          items not shipped in a box, the outside casing is considered the shipping container.
          Please note that general wear and tear, along with damage to protruding parts such as
          wheels, straps, pockets, handles, or other attachments on a travel case or bag, are not
          covered under this protection.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'How do I file a damage/missing item claim?',
      answer: (
        <>
          In order to file a damage or missing items claim, a{' '}
          <a href="https://www.shipsticks.com/claims">claims form</a> must be submitted to a claims
          representative. Claims requirements are outlined on our claims form.
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
    {
      question: 'How long does it take to resolve a claim?',
      answer: (
        <>
          <p>
            A claims specialist will review and file your claim within 1-2 business days. If
            additional information is needed, a claims specialist will contact you by phone or
            email. Otherwise, you will be made aware of your claim being filed.
          </p>
          <p>
            Once your claim has been reviewed and filed, you can expect to receive an approval or
            denial within 14 business days. Once the claim has been filed, it cannot be altered. All
            decisions determined by our 3rd party insurance carrier are final.
          </p>
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
    {
      question: 'Can I ship luggage, push carts, or oversized items?',
      answer: (
        <>
          Yes! You can ship luggage along with your golf clubs. For specialty items like push carts
          or oversized gear, just <a href="https://www.shipsticks.com/contact">contact our team</a>{' '}
          - we&apos;re happy to help arrange those shipments.
        </>
      ),
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      question: 'How do I get a custom quote for special shipments?',
      answer: (
        <>
          To get a custom quote for specialty or oversized shipments, please{' '}
          <a href="https://www.shipsticks.com/contact">contact</a> our customer support team with
          your shipment details. We&apos;ll work with you to provide a personalized price and
          shipping plan.
        </>
      ),
      categoryName: 'Pricing & Insurance',
      categoryId: 5,
    },
    {
      question: 'Are there items you cannot ship?',
      answer: (
        <>
          Yes, there are certain items we cannot ship for safety and legal reasons. These include
          hazardous materials, perishable goods, and restricted items as outlined in our shipping
          policy. If you&apos;re unsure whether your item qualifies, please{' '}
          <a href="https://www.shipsticks.com/contact">contact</a> us before booking.
        </>
      ),
      categoryName: 'Shipping & Delivery',
      categoryId: 3,
    },
    {
      question: 'How do I contact customer support?',
      answer: (
        <>
          Click <a href="https://www.shipsticks.com/contact">here</a> to contact us.
        </>
      ),
      categoryName: 'Support & Claims',
      categoryId: 6,
    },
  ],
  button: {
    moreText: 'View More FAQs',
  },
  variant: 'white',
}

const newsletter: NewsletterSignupProps = {
  title: 'Stay up to date. Join our newsletter.',
  brand: 'shipplay',
}

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is ShipSticks?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'ShipSticks is a door-to-door shipping service that helps you travel more easily by ' +
          'sending your golf clubs, luggage, and other gear ahead of your trip. We pick up from your ' +
          'home, office, golf course, or hotel and deliver directly to your destination.<br><br>By ' +
          'shipping ahead with ShipSticks, you can skip the stress of hauling heavy bags through ' +
          'airports, waiting at baggage claim, and dealing with lost or delayed luggage.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I create an account?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          "Navigate to 'Log In' in the top right corner of your screen. You can either create " +
          'a new account or sign in to an existing one, but you will need one to book. Creating an' +
          ' account requires no commitment and only takes a few moments to complete.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is it free to create an account?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes, signing up is completely free! There's no cost or commitment to create an account.",
      },
    },
    {
      '@type': 'Question',
      name: 'Who can use ShipSticks?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Anyone can use ShipSticks! Whether you're a seasoned golfer, a first-time golf traveler, or shipping on behalf of someone else, our service is designed to make travel easier for everyone.",
      },
    },
    {
      '@type': 'Question',
      name: 'How do I book a shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Click <a href="https://www.shipsticks.com/ship">here</a> to get started. Simply enter your pickup and delivery locations, select your travel dates, and choose the service that best fits your needs.',
      },
    },
    {
      '@type': 'Question',
      name: 'What happens after I book?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Once you complete your booking, you'll receive your shipping labels and step-by-step instructions.",
      },
    },
    {
      '@type': 'Question',
      name: 'How many days in advance should I schedule my gear to arrive?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We suggest scheduling your gear to arrive at least one business day before your first round. For example, if your tee time is on Monday, plan to have your clubs delivered by the Friday before.',
      },
    },
    {
      '@type': 'Question',
      name: 'What if my local club isn’t a ShipSticks partner?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'That’s ok! You can still use ShipSticks, even if your local club isn’t an official partner. We do, however, recommend suggesting the service to your club pro, so you can start enjoying the convenience of shipping directly from your local pro shop.',
      },
    },
    {
      '@type': 'Question',
      name: 'What’s the difference between one-way and round-trip shipping?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'One-way shipping means your golf clubs or gear are shipped from your starting point to your destination. Round-trip shipping includes shipping your gear to your destination and then back to your home or original location. There is no cost difference between two one-way shipments and a round-trip booking.',
      },
    },
    {
      '@type': 'Question',
      name: 'What other items can I ship using ShipSticks?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Travel completely bag-free by shipping your luggage along with your golf clubs. We can accommodate carry-on, checked, and oversized luggage.<br><br>If you’re interested in shipping specialty items like push carts, or arranging group or event shipments, please <a href="https://www.shipsticks.com/contact">contact our team</a> so we can explore how to best accommodate your needs.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where can I ship my gear?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can send your shipment to any physical destination of your choosing, with the exception of P.O. boxes, airports, and countries we don’t currently service.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of bags/containers are allowed?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Pack your golf clubs just like you would for the airlines, in a soft travel bag or hard case. You may also package your clubs in a box, but it is not required. Luggage should be packed in a suitcase, duffel bag, or box.',
      },
    },
    {
      '@type': 'Question',
      name: 'How should I pack my clubs or luggage?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Click here for more information on <a href="https://www.shipsticks.com/pack">packing golf clubs and luggage</a>.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I lock my domestic shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If you’re shipping domestically within the U.S., your bag may be locked. However, we recommend leaving it unlocked to avoid any potential delays.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I lock my international shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No, you cannot lock your bag when shipping internationally. All bags must be unlocked to ensure customs inspections can be performed if necessary.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are there size or weight limits?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Please identify your bag’s maximum dimensions (length + width + height) and weight to determine which bag size category it fits into. If your shipment exceeds the size or weight limits for that category, please <a href="https://www.shipsticks.com/contact">contact our team</a> - we’ll be happy to provide a custom quote for your shipment. Examples: If you have a suitcase that weighs 24 lbs with a maximum dimension of 60 inches, it should be booked as Checked Luggage. If you have a golf bag that weighs 55 lbs with a maximum dimension of 70 inches, it should be booked as an XL Golf Bag. Bag Type Maximum Dimension Maximum Weight Standard Golf Bag 72 inches 42 lbs XL Golf Bag 79 inches 56 lbs Carry-on Luggage 45 inches 25 lbs Checked Luggage 62 inches 50 lbs Oversized Luggage 75 inches 65 lbs',
      },
    },
    {
      '@type': 'Question',
      name: 'What happens if my bag is overweight?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If your bag weighs more than the limit for your chosen size, there could be extra fees and possible delays. To keep things running smoothly, we recommend sizing up if you’re unsure - better safe than sorry!',
      },
    },
    {
      '@type': 'Question',
      name: 'What if I can’t weigh my bag?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your bag weighed. If the weight is significantly heavier than expected, additional fees may apply.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I attach the shipping labels?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "If you're using a hard travel case or box, attach your label with clear packing tape, making sure the barcode is visible and secure. If your bag doesn't have a hard surface, you'll need to place the label on a luggage baggage tag (also known as a fly tag), which you can pick up from your local FedEx, UPS, or DHL location, and attach it to the handle. Don't forget to put a second copy of your shipping label in your bag, too.",
      },
    },
    {
      '@type': 'Question',
      name: 'What if I don’t have a printer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can email the shipping label to your local FedEx, UPS, or DHL for printing. Or, if time allows, for an additional fee we can mail printed labels directly to you - just give us a heads-up when placing your order.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does ShipSticks provide packaging?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We don’t supply packaging, but no worries - you can simply use your own hard case, soft travel bag, or box to ship your gear.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need a box?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Nope, no box needed! If you’re shipping a golf bag in an airline-approved soft travel bag or hard case, you’re good to go. Same goes for luggage - as long as it’s packed in a suitcase or duffle bag.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where do I get a box?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If you have a soft travel bag or hard case, you don’t need a box - that’s the beauty of our service. However, if you need a box, you can usually buy one at your local UPS or FedEx store for a small fee (prices vary by location). We also sell golf bag-sized boxes - just reach out to Customer Support to order. Here’s our pricing and shipping times for boxes:<br>Ground Shipping (5–7 Business Days)<br>1 Box: $29.99<br>2 Boxes: $34.99<br>Express Shipping (1–3 Business Days)<br>1 Box: $39.99<br>2 Boxes: $44.99',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I have to pack my bag differently if I am shipping internationally?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For international shipments, no locks are allowed, including TSA locks, since customs may need to inspect your package. Here’s what you can include in your golf bag: clubs, bags, balls, golf shoes, and rain gear like umbrellas or jackets and pants. For luggage, you can pack worn clothing, new clothing with price tags removed, footwear, headwear, and undergarments.',
      },
    },
    {
      '@type': 'Question',
      name: 'What can I include in my shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Here’s what you can include in your golf bag: clubs, bags, balls, golf shoes, and rain gear like umbrellas or jackets and pants. For luggage, you can pack worn clothing, new clothing with price tags removed, footwear, headwear, and undergarments.',
      },
    },
    {
      '@type': 'Question',
      name: 'What items are prohibited?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Some things are off-limits, like toiletries (soap, shampoo, toothpaste), medications, pressurized containers (aerosol cans or CO2 cartridges), cash or negotiable items, electronics like rangefinders and GPS devices, batteries, alcohol or anything containing alcohol (including perfume and cologne), tobacco, firearms and ammunition, and food, vitamins, or other consumables. Before shipping, please remove all price tags. For the full list of prohibited items, check out our Terms of Use.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add luggage to my order?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Adding pieces of luggage to your order is simple. Just scroll down to the traveler section, below the pickup and delivery dates, and you’ll see an option labeled Luggage Count. From the dropdown menu, select the number of pieces you want to ship and choose the type—carry-on, checked, or oversized bag.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add hotel check-in information to my shipping label?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, under ‘Other Info’ on the booking page, you can type in any information you would like. For example: Confirmation Number XXXXX, Checking in XX/XX/XX, Guest of John Smith, etc.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who will be picking up my shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'One of our trusted logistics partners, either FedEx, UPS, or DHL, will pick up your clubs or luggage. Your shipping label will indicate which carrier will handle your pickup.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I get my golf clubs picked up from my home?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! We can pick your clubs up from your home, office, or golf course - or you can simply drop them off at your nearest carrier store for added convenience.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a fee for dropping off my golf clubs?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'FedEx Offices and UPS Stores do not charge fees to drop off. However, some third-party pickup and drop-off locations may charge package handling fees. These fees are separate from our service and can vary depending on the location. To avoid surprises, we recommend calling ahead to check if any fees apply.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I choose my carrier drop-off location?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'While we assign your carrier (FedEx, UPS, or DHL) when you book, you’re free to drop off your shipment at any local location serviced by that carrier. Click <a href="https://www.shipsticks.com/store_locator">here</a> to find a drop-off location near you.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I have to be home when my bags are picked up?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You don’t need to be home for pickup. Just leave your securely packed and labeled shipment outside your front door during the pickup window, at your discretion. If you don’t want to leave it unattended and can’t be home, you can drop it off at a local FedEx, UPS, or DHL store - just make sure it matches the carrier listed on your shipping label.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I cancel my order?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, you may cancel your order using the ‘Edit Shipment’ link in your confirmation email, tracking page, or by logging into your account and selecting the shipment you want to cancel.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I make changes to my order?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! You can update your order using the ‘Edit Shipment’ link in your confirmation email, tracking page, or by logging into your account and selecting the shipment you want to modify. Just make sure to do it before your shipment is in transit. Once it’s on the move, you’ll need to contact customer support to request changes, and fees may apply.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does delivery take?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Delivery times vary depending on the service level you choose. Ground shipments typically arrive within 2 to 6 business days, depending on the route, while expedited options can get your gear there even faster - some even as quickly as the next business day. After pickup, you’ll receive tracking updates so you can monitor your shipment every step of the way.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a signature required upon delivery?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A signature is not required, but it’s ultimately up to the driver’s discretion. If the driver considers the delivery area secure or your destination has a shipping/receiving area, your shipment will be delivered without a signature. If this is a concern, we recommend shipping to a supervised location, like a business, golf course, resort, friend or family member, or carrier store (fees may apply).',
      },
    },
    {
      '@type': 'Question',
      name: 'Does ShipSticks deliver on weekends?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled during regular business days when all of our partnered carriers are operating. That said, depending on the route, your gear might still arrive on a weekend.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to confirm that the golf course, resort, rental home, or hotel will accept my shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Before placing your order, we do recommend reaching out to your destination to let them know about your shipment and your expected arrival date.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do hotels and golf courses charge to receive/hold my gear?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Policies vary by location. Some properties may charge a fee for receiving or holding shipments, so it’s best to check directly with them beforehand.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I have my bag returned to a different address than the one it was originally shipped from?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'That’s easy! Just book two separate one-way shipments - one to send your bag out, and another to have it returned to a different address. Don’t worry, the cost is the same as a round-trip shipment. This also works if your trip has multiple stops and you need your bag sent to different places along the way.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I track my shipment?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "We'll keep you posted with email updates and optional text alerts when your shipment is picked up, out for delivery, and delivered. You'll also find a tracking link in your confirmation email. To track through your account, simply:<br>Visit www.ShipSticks.com<br>Log in (top-right corner)<br>Go to My Account > Shipments<br>Select the shipment you'd like to track",
      },
    },
    {
      '@type': 'Question',
      name: 'Will I get notifications for delivery updates?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! You’ll receive real-time tracking notifications via email so you always know where your shipment is and when to expect delivery. You can opt-in to receive notifications via text as well.',
      },
    },
    {
      '@type': 'Question',
      name: 'What if my shipment is delayed or lost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Although delays and losses are rare, if we suspect either might have occurred, our customer support team will reach out to you. We’ll promptly investigate and work to resolve the issue as quickly as possible.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you ship internationally?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We service 180 countries and territories, and handle all the tricky customs paperwork.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does it cost to ship golf clubs?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The cost to ship golf clubs varies depending on factors like your shipping distance and the service level you choose. To get an exact quote, simply enter your pickup and delivery locations on our website or contact our customer support team for personalized assistance.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is insurance included? Can I add more coverage?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Domestic shipments come with $1,000 of insurance for golf bags and $500 for luggage. International shipments include $500 for either. Need more coverage? You can add extra for a small fee. We recommend choosing an amount that reflects the full value of your gear and the bag it’s packed in.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is covered under insurance?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "ShipSticks' shipment insurance protects against complete loss of your shipped item or against any physical damage to qualified items that has occurred while in transit. For items not shipped in a box, the outside casing is considered the shipping container and protruding parts such as wheels, straps, pockets, handles, or other items…attached to a travel case or bag will not be covered.",
      },
    },
    {
      '@type': 'Question',
      name: 'How do I file a damage/missing item claim?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In order to file a damage or missing items claim, a claims form must be submitted to a claims representative. Claims requirements are outlined on our claims form.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does it take to resolve a claim?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A claims specialist will review and file your claim within 1-2 business days. If additional information is needed, a claims specialist will contact you by phone or email. Otherwise, you will be made aware of your claim being filed.<br><br>Once your claim has been reviewed and filed, you can expect to receive an approval or denial within 14 business days. Once the claim has been filed, it cannot be altered. All decisions determined by our 3rd party insurance carrier are final.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I ship luggage, push carts, or oversized items?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes! You can ship luggage along with your golf clubs. For specialty items like push carts or oversized gear, just <a href='https://www.shipsticks.com/contact'>contact our team</a> - we're happy to help arrange those shipments.",
      },
    },
    {
      '@type': 'Question',
      name: 'How do I get a custom quote for special shipments?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "To get a custom quote for specialty or oversized shipments, please contact our customer support team with your shipment details. We'll work with you to provide a personalized price and shipping plan.",
      },
    },
    {
      '@type': 'Question',
      name: 'Are there items you cannot ship?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes, there are certain items we cannot ship for safety and legal reasons. These include hazardous materials, perishable goods, and restricted items as outlined in our shipping policy. If you're unsure whether your item qualifies, please contact us before booking.",
      },
    },
    {
      '@type': 'Question',
      name: 'How do I contact customer support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Click <a href="https://www.shipsticks.com/contact">here</a> to contact us.',
      },
    },
  ],
}

const content: FAQPageContent = {
  hero,
  faq,
  newsletter,
  structuredData,
}

export default content
